import type { IProxy, IProxyListItem } from '@hit-game/shared/types';
import { Activity, Check, Copy, Eye, EyeOff, Pencil, Trash2, User } from 'lucide-react';
import { useState } from 'react';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { formatRelativeTime, maskProxy } from '../../utils/format';

interface ProxyRowProps {
  proxy: IProxyListItem;
  /** Giá trị proxy đang được kiểm tra (khoá nút của mọi dòng). */
  checkingValue: string | null;
  onCheckHealth: (value: string) => void;
  onEdit: (proxy: IProxy) => void;
  onDelete: (id: string) => Promise<boolean>;
}

/** 3 trạng thái: sống / chết / chưa từng kiểm tra (`alive` = null). */
function statusOf(alive: boolean | null | undefined) {
  if (alive === true)
    return {
      dot: 'bg-success shadow-[0_0_6px_rgba(34,197,94,0.4)]',
      text: 'text-success',
      label: 'Live',
    };
  if (alive === false) return { dot: 'bg-danger', text: 'text-danger', label: 'Dead' };
  return { dot: 'bg-ink-faint', text: 'text-ink-dim', label: 'Chưa check' };
}

const ICON_BUTTON =
  'flex items-center rounded p-0.5 text-ink-ghost transition-all hover:bg-panel-hover hover:text-accent-bright';

export function ProxyRow({ proxy, checkingValue, onCheckHealth, onEdit, onDelete }: ProxyRowProps) {
  const [showFull, setShowFull] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, copy] = useCopyToClipboard();

  const status = statusOf(proxy.alive);
  const isChecking = checkingValue === proxy.value;

  const handleDelete = async () => {
    if (!proxy._id || deleting) return;
    setDeleting(true);
    const ok = await onDelete(proxy._id);
    setDeleting(false);
    // Xoá được thì cả dòng biến mất; hỏng thì thu nút xác nhận lại.
    if (!ok) setConfirmDelete(false);
  };

  return (
    <tr className="border-panel-hover hover:bg-panel-raised border-t transition-colors">
      <td className="w-[110px] px-5 py-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold">
          <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
          <span className={status.text}>{status.label}</span>
        </span>
      </td>

      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <code className="text-accent-bright font-mono text-[12px]">
            {showFull ? proxy.value : maskProxy(proxy.value)}
          </code>
          <button
            type="button"
            onClick={() => {
              setShowFull((v) => !v);
            }}
            title={showFull ? 'Ẩn' : 'Hiện đầy đủ'}
            className={ICON_BUTTON}
          >
            {showFull ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
          <button
            type="button"
            onClick={() => {
              copy(proxy.value);
            }}
            title="Sao chép"
            className={ICON_BUTTON}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
      </td>

      <td className="px-5 py-3">
        {/* Tên tài khoản, không phải "đã gán": biết đúng ai đang dùng mới dám sửa/xoá proxy.
            Có `accountId` mà không có tên = tài khoản đã bị xoá, proxy còn kẹt lại. */}
        {proxy.accountUsername ? (
          <span className="bg-accent-soft text-accent inline-flex max-w-[220px] items-center gap-1.5 rounded px-2.5 py-0.5 text-[12px] font-medium">
            <User size={11} className="shrink-0" />
            <span className="truncate" title={proxy.accountUsername}>
              {proxy.accountUsername}
            </span>
          </span>
        ) : (
          <span className="bg-panel-hover text-ink-muted inline-flex items-center gap-1.5 rounded px-2.5 py-0.5 text-[12px]">
            <User size={11} />
            {proxy.accountId ? 'Tài khoản đã xoá' : '-'}
          </span>
        )}
      </td>

      <td className="px-5 py-3">
        <span className="text-ink-dim text-[11px]">
          {proxy.lastChecked ? formatRelativeTime(proxy.lastChecked) : 'Chưa check'}
        </span>
      </td>

      <td className="w-[130px] px-5 py-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              onCheckHealth(proxy.value);
            }}
            disabled={checkingValue !== null}
            title="Kiểm tra proxy"
            className="text-ink-dim hover:bg-panel-hover hover:text-success flex items-center rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Activity size={14} className={isChecking ? 'text-accent animate-spin' : ''} />
          </button>

          <button
            type="button"
            onClick={() => {
              onEdit(proxy);
            }}
            title="Sửa proxy"
            className="text-ink-dim hover:bg-panel-hover hover:text-accent flex items-center rounded p-1 transition-colors"
          >
            <Pencil size={14} />
          </button>

          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  void handleDelete();
                }}
                disabled={deleting}
                className="text-danger border-danger-border bg-danger-deep hover:bg-danger-border rounded border px-2 py-0.5 text-[11px] font-bold transition-colors disabled:opacity-50"
              >
                {deleting ? '...' : 'Xóa'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                }}
                disabled={deleting}
                className="border-line bg-panel-hover text-ink-dim hover:bg-line-soft rounded border px-2 py-0.5 text-[11px] font-bold transition-colors disabled:opacity-50"
              >
                Hủy
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setConfirmDelete(true);
              }}
              title="Xoá"
              className="text-ink-dim hover:bg-line-soft hover:text-danger flex items-center rounded p-1 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
